import React from "react";

import { useTranslation } from "react-i18next";

import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Skeleton } from "@/src/components/ui/skeleton";
import { cn } from "@/src/lib/utils";

import { CHART_HEIGHT_CLASS } from "./chartLayout";
import ExportDisabledContext from "./ExportDisabledContext";
import NotFilterableByOfferHint from "./NotFilterableByOfferHint";

import type { JSX, ReactNode } from "react";

import type { ChartCardStatus } from "./charts.types";

interface ChartCardProps {
  title: string;
  status: ChartCardStatus;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  isFetching?: boolean;
  isNotFilterableByOffer?: boolean;
}

function ChartCard({
  title,
  status,
  children,
  actions,
  className,
  isFetching,
  isNotFilterableByOffer,
}: ChartCardProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");

  const renderContent = (): ReactNode => {
    switch (status) {
      case "loading":
        return <Skeleton className={cn(CHART_HEIGHT_CLASS, "w-full")} />;
      case "error":
        return (
          <p
            className={cn(
              "text-destructive flex items-center justify-center text-sm",
              CHART_HEIGHT_CLASS,
            )}
          >
            {t("charts.error")}
          </p>
        );
      case "empty":
        return (
          <p
            className={cn(
              "text-muted-foreground flex items-center justify-center text-sm",
              CHART_HEIGHT_CLASS,
            )}
          >
            {t("charts.noData")}
          </p>
        );
      default:
        return (
          <div className={cn("transition-opacity", isFetching && "opacity-50")}>
            {children}
          </div>
        );
    }
  };

  return (
    <Card className={cn("gap-4", className)}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {isNotFilterableByOffer ? (
          <CardDescription>
            <NotFilterableByOfferHint />
          </CardDescription>
        ) : null}
        {actions ? (
          <ExportDisabledContext.Provider value={status !== "ready"}>
            <CardAction className="flex items-center gap-2">
              {actions}
            </CardAction>
          </ExportDisabledContext.Provider>
        ) : null}
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
}

export default ChartCard;
